/**
 * This service manages local storage operations related to impersonation and recent tenant selections.
 * It supports storing, retrieving, and updating information about tenant organizations and their roles
 * to enable seamless impersonation workflows within the application.
 *
 * Interfaces:
 * - ImpersonationResponse: Union type representing various possible response shapes.
 * - ChargeyeOrganizationInfoStorage: Structure used to store original and impersonated tenant info.
 * - OrganizationCache: Represents a cached organization with role metadata.
 *
 * Responsibilities:
 * - Cache and retrieve recent tenant selections with roles and system roles.
 * - Store original and impersonated tenant IDs in localStorage.
 * - Support impersonation-aware operations such as retrieving selected tenant roles.
 */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ImpersonateV2Response } from '@kempoweroyj/ngx-iam-organizations-api-client';
import { isValidString } from '../../utils/isValidString';

export type ImpersonationResponse = DataResponse | ErrorResponse | StatusResponse | StatusErrorResponse | null;
interface ImpersonatedStorage {
    tenantId: string;
    tenantName: string;
}
interface ChargeyeOrganizationInfoStorage {
    originalTenantId: string;
    originalTenantName: string;
    tenantId?: string;
    tenantName?: string;
    impersonated?: ImpersonatedStorage;
}

interface DataResponse {
    data: string;
    error?: string;
    status?: string;
}

interface ErrorResponse {
    error: ImpersonateV2Response;
    data?: Record<string, unknown>;
    status?: string;
}

interface StatusResponse {
    status: string;
    error?: string;
    data?: Record<string, unknown>;
}

export interface OrganizationCache {
    name: string;
    id: string;
    tenantId?: string;
    roles?: string[];
    systemRoles?: string[];
}

type Organizations = {
    [key: string]: OrganizationCache;
};

interface StatusErrorResponse {
    status: string;
    error: HttpErrorResponse | undefined;
    data?: Record<string, unknown>;
}

@Injectable()
export class ImpersonationLocalCacheService {
    /**
     * Retrieves cached roles and system roles for the selected tenant based on tenant name.
     * @param tenantName - The name of the tenant to match in cached organizations.
     * @returns An object containing roles and systemRoles, or null if not found.
     */
    getSelectedTenantCachedRoles(tenantName: string) {
        const recentSelections: Organizations = this.getRecentCachedOrganisations();

        if (recentSelections && typeof recentSelections === 'object' && !Array.isArray(recentSelections)) {
            const result = Object.values(recentSelections).find((org) => org.name === tenantName) as OrganizationCache;
            if (result) {
                const { roles = [], systemRoles = [] } = result;
                return { roles, systemRoles };
            }
        }
        return null;
    }

    /**
     * Retrieves recently cached tenant organizations from localStorage.
     * @returns A mapping of recently visited organizations or null if unavailable.
     */
    getRecentCachedOrganisations() {
        const storedData = localStorage.getItem('chargeye-recent-orgs');
        const storedOrgs = storedData !== null ? JSON.parse(storedData) : null;
        if (storedOrgs) {
            if (storedOrgs['orgVisited']) {
                return storedOrgs['orgVisited'];
            }
            this.clearRecentOrganizationsStorage();
        }
        return null;
    }

    /**
     * Clears the recent organization cache from localStorage.
     */
    clearRecentOrganizationsStorage() {
        localStorage.removeItem('chargeye-recent-orgs');
    }

    /**
     * Caches the current impersonated tenant along with roles and system roles into localStorage.
     * Maintains only the latest 3 entries.
     * @param impersonatedTenantId - The ID of the impersonated tenant.
     * @param roles - Array of assigned roles.
     * @param systemRoles - Array of assigned system roles.
     * @param name - The tenant display name.
     */
    cacheCurrentOptions(impersonatedTenantId: string, roles: string[], systemRoles: string[], name: string) {
        const storedOrgs = this.getRecentCachedOrganisations();
        let recentSelections: Organizations = {};

        if (storedOrgs !== null) {
            recentSelections = this.removeCachedOrganizationInfo(storedOrgs, impersonatedTenantId);
            recentSelections = this.selecteLatestThreeSelections(recentSelections);
        }

        const timestamp = Date.now();

        recentSelections[timestamp] = {
            name: name,
            id: impersonatedTenantId,
        };

        if (roles.length > 0) {
            recentSelections[timestamp]['roles'] = roles;
        }

        if (systemRoles.length > 0) {
            recentSelections[timestamp]['systemRoles'] = systemRoles;
        }

        const cashedData = { ['orgVisited']: recentSelections };
        const string = JSON.stringify(cashedData);
        localStorage.setItem('chargeye-recent-orgs', string);
    }

    /**
     * Removes any cached organization entries that match the current impersonated tenant ID.
     * @param recentSelections - Existing selection map.
     * @param impersonatedTenantId - Tenant ID to exclude.
     * @returns Filtered selection map.
     */
    protected removeCachedOrganizationInfo(recentSelections: Organizations, impersonatedTenantId: string) {
        return Object.keys(recentSelections).reduce((acc, key: string) => {
            if (recentSelections[key].id !== impersonatedTenantId) {
                acc[key] = recentSelections[key];
            }
            return acc;
        }, {} as Organizations);
    }

    /**
     * Selects the first three most recent cached organization selections.
     * @param recentSelections - Existing selection map.
     * @returns A map limited to the first three entries.
     */
    protected selecteLatestThreeSelections(recentSelections: Organizations) {
        return Object.entries(recentSelections)
            .slice(0, 3)
            .reduce<{ [key: string]: OrganizationCache }>((acc, [key, value]) => {
                acc[key] = value;
                return acc;
            }, {} as Organizations);
    }

    /**
     * Stores original tenant information in localStorage if both tenant ID and name are valid strings.
     * @param orignalTenancyData - Contains original tenant ID and name.
     */
    addOrginalTenantIdToStorage(orignalTenancyData: ChargeyeOrganizationInfoStorage) {
        const { originalTenantId, originalTenantName } = orignalTenancyData;

        if (isValidString(originalTenantId) && isValidString(originalTenantName)) {
            const storedData = localStorage.getItem('chargeye-organization-info');
            if (storedData === null) {
                localStorage.setItem('chargeye-organization-info', JSON.stringify(orignalTenancyData));
            }
        }
    }

    /**
     * Adds impersonated tenant information to existing original tenant storage entry.
     * @param orignalTenancyData - Contains impersonated tenant ID and name.
     */
    addImpersonatedTenantIdToStorage(orignalTenancyData: ImpersonatedStorage) {
        const { tenantId, tenantName } = orignalTenancyData;

        if (isValidString(tenantId) && isValidString(tenantName)) {
            const storedData = localStorage.getItem('chargeye-organization-info');
            if (storedData !== null) {
                const originalData = JSON.parse(storedData);

                originalData.impersonated = {
                    tenantId: tenantId,
                    tenantName: tenantName,
                };
                localStorage.setItem('chargeye-organization-info', JSON.stringify(originalData));
            }
        }
    }
}
